"use client";

import { useEffect, useRef, useState } from "react";
import { googleAuth, facebookAuth } from "@/lib/api/auth";
import Button from "@/components/ui/Button";
import type { RegisterData } from "@/lib/types";

type SocialSignupRole = RegisterData["role"];

// ─── TypeScript declarations for browser SDKs ────────────────────────────────

declare global {
  interface Window {
    google?: {
      accounts: {
        id: {
          initialize: (config: object) => void;
          renderButton: (el: HTMLElement, config: object) => void;
          prompt: () => void;
        };
      };
    };
    FB?: {
      init: (config: object) => void;
      login: (
        callback: (response: { authResponse?: { accessToken: string } }) => void,
        options: object
      ) => void;
    };
    fbAsyncInit?: () => void;
  }
}

// ─── Props ───────────────────────────────────────────────────────────────────

interface SocialAuthButtonsProps {
  mode: "login" | "register";
  selectedRole?: SocialSignupRole | null;
  onSuccess: (data: unknown) => void;
  onError: (message: string) => void;
}

// ─── Constants ───────────────────────────────────────────────────────────────

const LINKEDIN_REDIRECT_URI =
  typeof window !== "undefined"
    ? `${window.location.origin}/auth/linkedin/callback`
    : "http://localhost:3000/auth/linkedin/callback";

const LINKEDIN_SCOPE = "r_liteprofile r_emailaddress";

function isConfiguredClientId(clientId: string | undefined): clientId is string {
  return !!clientId && !clientId.includes('your-') && !clientId.includes('placeholder');
}

// ─── Social icons ────────────────────────────────────────────────────────────

function FacebookIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M24 12.073c0-6.627-5.373-12-12-12s-12 5.373-12 12
           c0 5.99 4.388 10.954 10.125 11.854v-8.385H7.078v-3.47h3.047V9.43
           c0-3.007 1.792-4.669 4.533-4.669 1.312 0 2.686.235 2.686.235v2.953
           H15.83c-1.491 0-1.956.925-1.956 1.874v2.25h3.328l-.532 3.47h-2.796
           v8.385C19.612 23.027 24 18.062 24 12.073z"
      />
    </svg>
  );
}

function LinkedInIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} aria-hidden="true">
      <path
        d="M20.447 20.452h-3.554v-5.569c0-1.328-.027-3.037-1.852-3.037
           -1.853 0-2.136 1.445-2.136 2.939v5.667H9.351V9h3.414v1.561h.046
           c.477-.9 1.637-1.85 3.37-1.85 3.601 0 4.267 2.37 4.267 5.455v6.286z
           M5.337 7.433a2.062 2.062 0 01-2.063-2.065 2.064 2.064 0 112.063 2.065z
           m1.782 13.019H3.555V9h3.564v11.452zM22.225 0H1.771C.792 0 0 .774 0
           1.729v20.542C0 23.227.792 24 1.771 24h20.451C23.2 24 24 23.227 24
           22.271V1.729C24 .774 23.2 0 22.222 0h.003z"
      />
    </svg>
  );
}

// ─── Component ───────────────────────────────────────────────────────────────

export function SocialAuthButtons({
  mode,
  selectedRole,
  onSuccess,
  onError,
}: SocialAuthButtonsProps) {
  const googleBtnRef = useRef<HTMLDivElement>(null);
  const [loadingProvider, setLoadingProvider] = useState<string | null>(null);
  const googleClientId = process.env.NEXT_PUBLIC_GOOGLE_CLIENT_ID;
  const facebookAppId = process.env.NEXT_PUBLIC_FACEBOOK_APP_ID;
  const linkedInClientId = process.env.NEXT_PUBLIC_LINKEDIN_CLIENT_ID;
  const showGoogleButton = isConfiguredClientId(googleClientId);
  const showFacebookButton = isConfiguredClientId(facebookAppId);
  const showLinkedInButton = isConfiguredClientId(linkedInClientId);
  const hasActiveProvider = showGoogleButton || showFacebookButton || showLinkedInButton;
  const requiresRole = mode === "register";
  const socialRole = requiresRole ? selectedRole : undefined;
  const roleMissing = requiresRole && !socialRole;

  const getErrorMessage = (error: unknown, fallback: string) => {
    return String((error as { message?: string })?.message || fallback);
  };

  // ── Google ───────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showGoogleButton) {
      console.info('[Google Auth] Client ID not configured — skipping Google sign-in initialization. Set NEXT_PUBLIC_GOOGLE_CLIENT_ID to enable.');
      return;
    }

    const initGoogle = () => {
      if (!window.google || !googleBtnRef.current || roleMissing) return;

      window.google.accounts.id.initialize({
        client_id: googleClientId,
        callback: async (response: { credential: string }) => {
          setLoadingProvider("google");
          try {
            const data = await googleAuth(response.credential, socialRole ?? undefined);
            onSuccess(data);
          } catch (error) {
            onError(getErrorMessage(error, "Google sign-in failed. Please try again."));
          } finally {
            setLoadingProvider(null);
          }
        },
      });

      window.google.accounts.id.renderButton(googleBtnRef.current, {
        theme: "outline",
        size: "large",
        width: googleBtnRef.current.offsetWidth || 360,
        text: mode === "login" ? "signin_with" : "signup_with",
        shape: "rectangular",
        logo_alignment: "left",
      });
    };

    // Poll until SDK is loaded (it's async)
    const timer = setInterval(() => {
      if (window.google) {
        initGoogle();
        clearInterval(timer);
      }
    }, 100);

    return () => clearInterval(timer);
  }, [mode, onSuccess, onError, googleClientId, showGoogleButton, roleMissing, socialRole]);

  // ── Facebook ─────────────────────────────────────────────────────────────
  useEffect(() => {
    if (!showFacebookButton) {
      console.info('[Facebook Auth] App ID not configured — skipping Facebook sign-in initialization. Set NEXT_PUBLIC_FACEBOOK_APP_ID to enable.');
      return;
    }

    window.fbAsyncInit = () => {
      window.FB!.init({
        appId: facebookAppId,
        cookie: false,
        xfbml: false,
        version: "v19.0",
      });
    };
  }, [facebookAppId, showFacebookButton]);

  const handleFacebook = () => {
    if (roleMissing) {
      onError("Choose your role before creating a social account.");
      return;
    }
    if (!window.FB) {
      onError("Facebook SDK not loaded. Please refresh and try again.");
      return;
    }
    setLoadingProvider("facebook");
    window.FB.login(
      async (response) => {
        if (response.authResponse?.accessToken) {
          try {
            const data = await facebookAuth(response.authResponse.accessToken, socialRole ?? undefined);
            onSuccess(data);
          } catch (error) {
            onError(getErrorMessage(error, "Facebook sign-in failed. Please try again."));
          } finally {
            setLoadingProvider(null);
          }
        } else {
          setLoadingProvider(null);
          // User cancelled — no error needed
        }
      },
      { scope: "email,public_profile" }
    );
  };

  // ── LinkedIn ─────────────────────────────────────────────────────────────
  const handleLinkedIn = () => {
    if (roleMissing) {
      onError("Choose your role before creating a social account.");
      return;
    }
    const clientId = linkedInClientId;
    if (!isConfiguredClientId(clientId)) {
      console.info('[LinkedIn Auth] Client ID not configured — skipping LinkedIn sign-in initialization. Set NEXT_PUBLIC_LINKEDIN_CLIENT_ID to enable.');
      return;
    }

    const state = JSON.stringify({ role: socialRole ?? null });
    const params = new URLSearchParams({
      response_type: "code",
      client_id: clientId,
      redirect_uri: LINKEDIN_REDIRECT_URI,
      scope: LINKEDIN_SCOPE,
      state,
    });
    window.location.href = `https://www.linkedin.com/oauth/v2/authorization?${params}`;
  };

  useEffect(() => {
    if (!showLinkedInButton) {
      console.info('[LinkedIn Auth] Client ID not configured — skipping LinkedIn sign-in initialization. Set NEXT_PUBLIC_LINKEDIN_CLIENT_ID to enable.');
    }
  }, [showLinkedInButton]);

  if (!hasActiveProvider) {
    return null;
  }

  // ── Render ───────────────────────────────────────────────────────────────
  return (
    <div className="flex flex-col gap-3">
      {showGoogleButton && (
        <>
          {/* Google — rendered by Google's own SDK */}
          {roleMissing ? (
            <Button
              type="button"
              variant="outline"
              disabled
              className="w-full min-h-11 border-foreground/10 bg-card text-ash"
            >
              Choose a role to sign up with Google
            </Button>
          ) : (
            <div ref={googleBtnRef} className="w-full min-h-[44px]" />
          )}
        </>
      )}

      {showFacebookButton && (
        <Button
          type="button"
          variant="outline"
          onClick={handleFacebook}
          disabled={!!loadingProvider || roleMissing}
          className="w-full min-h-11 border-foreground/10 bg-card hover:bg-muted hover:text-foreground"
        >
          {loadingProvider === "facebook" ? (
            <span className="size-4 animate-spin rounded-full border-2 border-muted-foreground border-t-transparent" />
          ) : (
            <FacebookIcon className="size-5 fill-[#1877F2]" />
          )}
          {mode === "login" ? "Continue with Facebook" : "Sign up with Facebook"}
        </Button>
      )}

      {showLinkedInButton && (
        <Button
          type="button"
          variant="outline"
          onClick={handleLinkedIn}
          disabled={!!loadingProvider || roleMissing}
          className="w-full min-h-11 border-foreground/10 bg-card hover:bg-muted hover:text-foreground"
        >
          <LinkedInIcon className="size-5 fill-[#0A66C2]" />
          {mode === "login" ? "Continue with LinkedIn" : "Sign up with LinkedIn"}
        </Button>
      )}

      {hasActiveProvider && (
        <div className="relative my-1">
          <div className="absolute inset-0 flex items-center">
            <span className="w-full border-t border-border" />
          </div>
          <div className="relative flex justify-center text-xs uppercase">
            <span className="bg-card px-3 text-muted-foreground">
              or continue with email
            </span>
          </div>
        </div>
      )}
    </div>
  );
}
